import React, { useContext, useEffect, useRef, useState } from 'react';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import ChatNavigationItem, {
  ChatNavigationItemLoading,
  NavigationItem
} from 'buyside/components/presentation/ChatLayout/ChatNavigation/chatNavigationItem';
import { ChatContext } from 'buyside/components/presentation/ChatLayout/chatLayout';

import { BREAKPOINTS, useIsMobile } from 'buyside/util/hooks/useIsMobile';

import styles from './chatNavigationStyles.scss';

export type UseChatNavigationType = {
  loading: boolean;
  indexNavigationItem?: NavigationItem;
  navigationItems: NavigationItem[];
  navigationTitle: string;
};

const ChatNavigation: React.FC = () => {
  const navigationItemsRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  const chatContext = useContext(ChatContext);

  if (!chatContext) throw new Error();

  const handleCloseMobileNavigation = (): void => setMobileNavigationOpen(false);

  const { useChatNavigation } = chatContext;
  const { loading, indexNavigationItem, navigationItems, navigationTitle } = useChatNavigation();

  useEffect(() => {
    if (!mobileNavigationOpen) return;

    const handleOnOutsideClick = (e: MouseEvent): void => {
      if (navigationItemsRef.current && !navigationItemsRef.current.contains(e.target as Node)) {
        handleCloseMobileNavigation();
      }
    };

    document.addEventListener('click', handleOnOutsideClick, true);

    return (): void => {
      document.removeEventListener('click', handleOnOutsideClick, true);
    };
  }, [mobileNavigationOpen]);

  return (
    <div className={styles.chatNavigation}>
      <div className={styles.chatNavigationHeader}>
        <button
          className={styles.toggleNavigationButton}
          onClick={() => setMobileNavigationOpen(prev => !prev)}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <h4>{navigationTitle}</h4>
      </div>
      <div
        className={classNames(styles.chatNavigationItems, {
          [styles.navigationClosed]: !mobileNavigationOpen
        })}
        ref={navigationItemsRef}
      >
        {isMobile ? (
          <button className={styles.toggleNavigationButton} onClick={handleCloseMobileNavigation}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : null}
        {indexNavigationItem ? (
          <ChatNavigationItem {...indexNavigationItem} onClick={handleCloseMobileNavigation} />
        ) : null}
        {loading
          ? [...Array(6)].map((_e, rowIndex) => (
              <ChatNavigationItemLoading key={`navigation-item-loading-${rowIndex}`} />
            ))
          : null}
        {navigationItems.map(navigationItem => (
          <ChatNavigationItem
            key={`navigation-item-${navigationItem.title}`}
            {...navigationItem}
            onClick={handleCloseMobileNavigation}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatNavigation;
